export const SIMILARITY_THRESHOLD = 0.88;

export const ITEM_NAME_HEADERS = new Set([
  "itemname",
  "item_name",
  "item name",
  "emri i artikullit",
  "emriiartikullit",
  "emriartikullit",
  "emri",
  "artikulli",
]);

export const QUANTITY_HEADERS = new Set([
  "quantity",
  "qty",
  "sasia",
]);

export const PRICE_HEADERS = new Set([
  "price",
  "cmimi",
]);

export const VAT_HEADERS = new Set([
  "vat",
  "tvsh",
]);

export const UNIT_HEADERS = new Set([
  "unit",
  "unitid",
  "unit id",
  "njesia",
]);

export const BARCODE_HEADERS = new Set([
  "barcode",
  "barkodi",
]);

export const ITEM_ID_HEADERS = new Set([
  "itemid",
  "item id",
  "id",
]);

export const PRODUCT_NO_HEADERS = new Set([
  "productno",
  "product no",
  "product_no",
  "shifra",
]);

export const normalizeHeader = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const compactText = (value) => normalizeText(value).replace(/\s+/g, "");

export const stringSimilarity = (firstValue, secondValue) => {
  if (!firstValue || !secondValue) return 0;
  if (firstValue === secondValue) return 1;
  if (firstValue.length < 2 || secondValue.length < 2) return 0;

  const bigrams = new Map();

  for (let index = 0; index < firstValue.length - 1; index++) {
    const bigram = firstValue.substring(index, index + 2);
    bigrams.set(bigram, (bigrams.get(bigram) || 0) + 1);
  }

  let intersections = 0;

  for (let index = 0; index < secondValue.length - 1; index++) {
    const bigram = secondValue.substring(index, index + 2);
    const count = bigrams.get(bigram) || 0;

    if (count > 0) {
      bigrams.set(bigram, count - 1);
      intersections++;
    }
  }

  return (2 * intersections) / (firstValue.length + secondValue.length - 2);
};

export const tokenSimilarity = (firstValue, secondValue) => {
  const firstTokens = normalizeText(firstValue)
    .split(" ")
    .filter((token) => token.length > 1);
  const secondTokens = normalizeText(secondValue)
    .split(" ")
    .filter((token) => token.length > 1);

  if (!firstTokens.length || !secondTokens.length) return 0;

  const uniqueTokens = [...new Set(firstTokens)];
  const intersections = uniqueTokens.filter((token) =>
    secondTokens.includes(token)
  ).length;

  return intersections / Math.max(firstTokens.length, secondTokens.length);
};

export const containmentSimilarity = (firstValue, secondValue) => {
  const firstCompact = compactText(firstValue);
  const secondCompact = compactText(secondValue);

  if (!firstCompact || !secondCompact) return 0;
  if (
    !firstCompact.includes(secondCompact) &&
    !secondCompact.includes(firstCompact)
  ) {
    return 0;
  }

  const shorterLength = Math.min(firstCompact.length, secondCompact.length);
  const longerLength = Math.max(firstCompact.length, secondCompact.length);

  if (shorterLength < 4 || !longerLength) return 0;

  return shorterLength / longerLength;
};

export const findHeaderKey = (row, acceptedHeaders) => {
  if (!row) return null;

  return Object.keys(row).find((key) => {
    const normalizedKey = normalizeHeader(key)
      .replace(/[^a-z0-9_ ]+/g, "")
      .replace(/\s+/g, " ")
      .trim();
    const compactKey = normalizedKey.replace(/[^a-z0-9]+/g, "");

    return (
      acceptedHeaders.has(normalizedKey) || acceptedHeaders.has(compactKey)
    );
  });
};

export const buildPreparedItems = (items) => {
  const preparedItems = (items || [])
    .filter((item) => item?.itemName)
    .map((item) => ({
      ...item,
      normalizedItemName: normalizeText(item.itemName),
      compactItemName: compactText(item.itemName),
      normalizedBarcode: compactText(item.barcode),
      normalizedProductNo: compactText(item.productNo),
    }));

  const nameMap = new Map();
  const barcodeMap = new Map();
  const productNoMap = new Map();

  preparedItems.forEach((item) => {
    if (item.normalizedItemName && !nameMap.has(item.normalizedItemName)) {
      nameMap.set(item.normalizedItemName, item);
    }

    if (item.compactItemName && !nameMap.has(item.compactItemName)) {
      nameMap.set(item.compactItemName, item);
    }

    if (item.normalizedBarcode && !barcodeMap.has(item.normalizedBarcode)) {
      barcodeMap.set(item.normalizedBarcode, item);
    }

    if (item.normalizedProductNo && !productNoMap.has(item.normalizedProductNo)) {
      productNoMap.set(item.normalizedProductNo, item);
    }
  });

  return { preparedItems, nameMap, barcodeMap, productNoMap };
};

export const findBestItemMatch = (
  { itemName, barcode, productNo },
  preparedLookup
) => {
  const normalizedItemName = normalizeText(itemName);
  const compactItemName = compactText(itemName);
  const normalizedBarcode = compactText(barcode);
  const normalizedProductNo = compactText(productNo);

  if (normalizedBarcode && preparedLookup.barcodeMap.has(normalizedBarcode)) {
    const matchedItem = preparedLookup.barcodeMap.get(normalizedBarcode);
    return {
      matchedItem,
      matchType: "Exact",
      matchScore: 1,
      matchedBy: "barcode",
    };
  }

  if (
    normalizedProductNo &&
    preparedLookup.productNoMap.has(normalizedProductNo)
  ) {
    const matchedItem = preparedLookup.productNoMap.get(normalizedProductNo);
    return {
      matchedItem,
      matchType: "Exact",
      matchScore: 1,
      matchedBy: "productNo",
    };
  }

  if (!normalizedItemName) {
    return {
      matchedItem: null,
      matchType: "Not Found",
      matchScore: 0,
      matchedBy: "",
    };
  }

  const exactMatch =
    preparedLookup.nameMap.get(normalizedItemName) ||
    preparedLookup.nameMap.get(compactItemName);

  if (exactMatch) {
    return {
      matchedItem: exactMatch,
      matchType: "Exact",
      matchScore: 1,
      matchedBy: "itemName",
    };
  }

  let bestMatch = null;
  let bestScore = 0;

  preparedLookup.preparedItems.forEach((candidate) => {
    const score = Math.max(
      stringSimilarity(normalizedItemName, candidate.normalizedItemName),
      stringSimilarity(compactItemName, candidate.compactItemName),
      tokenSimilarity(normalizedItemName, candidate.normalizedItemName),
      containmentSimilarity(compactItemName, candidate.compactItemName)
    );

    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidate;
    }
  });

  if (bestScore >= SIMILARITY_THRESHOLD && bestMatch) {
    return {
      matchedItem: bestMatch,
      matchType: "Approx",
      matchScore: bestScore,
      matchedBy: "itemName",
    };
  }

  return {
    matchedItem: null,
    matchType: "Not Found",
    matchScore: bestScore,
    matchedBy: "",
  };
};
